import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import AccountModel from "../Models/Account/AccountModel";
import { Member } from "../Models/Member/Member.Current";
import ModalModel from "../Models/Modal/Modal.Model";
import NotificationsModel from "../Models/Notifications/Notifications.Model";
import { SwitchOrgModal } from "../Views/TicketAccess/SwitchOrgModal";

interface UseAccessOrgRedirectArgs {
  hasLoaded: boolean;
  resourceOrgId: number | null | undefined;
  realPath: string | null;
  /** e.g. "This ticket" / "This conversation" — forwarded to the switch-org modal. */
  resourceLabel: string;
}

interface UseAccessOrgRedirectResult {
  /** Show a spinner instead of the locked view — a same-org Member is about to redirect. */
  showSpinner: boolean;
}

// Same-org Members redirect away; cross-org-with-access get a switch prompt.
export function useAccessOrgRedirect({ hasLoaded, resourceOrgId, realPath, resourceLabel }: UseAccessOrgRedirectArgs): UseAccessOrgRedirectResult {
  const navigate = useNavigate();
  const myOrgId = Member.org?.id;
  const sameOrg = hasLoaded && myOrgId != null && resourceOrgId != null && myOrgId === resourceOrgId;

  const switchMembership = useMemo(() => {
    if (!hasLoaded || sameOrg || myOrgId == null || resourceOrgId == null) return undefined;
    return AccountModel.state.memberships?.find((m) => m.org.id === resourceOrgId);
  }, [hasLoaded, sameOrg, myOrgId, resourceOrgId]);

  useEffect(() => {
    if (sameOrg && realPath) navigate(realPath, { replace: true });
  }, [sameOrg, realPath, navigate]);

  const handleSwitchOrg = (): void => {
    if (!switchMembership || !realPath) return;
    AccountModel.selectMember(switchMembership);
    // Hard reload so account init picks up the new member.
    window.location.href = realPath;
  };

  useEffect(() => {
    if (!switchMembership) return;
    ModalModel.showModalWithContent(
      null,
      <SwitchOrgModal orgName={switchMembership.org.name} resourceLabel={resourceLabel} onSwitch={handleSwitchOrg} />,
      undefined,
      undefined,
      false
    );
    return () => ModalModel.hideModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switchMembership]);

  // Swivl admins can access any org, no membership row to switch to.
  useEffect(() => {
    if (!hasLoaded || sameOrg || switchMembership || resourceOrgId == null || !Member.isSwivlAdmin) return;
    void NotificationsModel.createToastNotification({
      message: "You haven't been added as a member of this org yet. Please reach out to Solutions for an invite."
    });
  }, [hasLoaded, sameOrg, switchMembership, resourceOrgId]);

  return { showSpinner: myOrgId != null && (!hasLoaded || sameOrg) };
}
